import { ParsedExcelSheet, ParsedExcelWorkbook } from './parseExcelFile';

export type BulkDepositValidationError = {
  depositId?: string;
  field?: string;
  value?: string;
  rowNumber?: number;
  sheetName?: string;
  strawNumber?: string;
  message: string;
};

export type BulkDepositHighlightedCell = {
  sheetName: string;
  rowIndex: number;
  columnIndex: number;
  message: string;
  errorIndex: number;
};

type ApiErrorPayload = {
  title?: unknown;
  detail?: unknown;
  errors?: unknown;
  message?: unknown;
};

type SheetAndColumn = {
  sheetName?: string;
  field?: string;
  value?: string;
};

const GENERIC_VALIDATION_TITLE = 'Validation failed for the payload';
const PAYLOAD_PREFIX = /^Validation failed for the payload:\s*/i;
const DEPOSIT_ID_PATTERN = /Deposit\s*ID\s*['"]([^'"]+)['"]/i;
const ROW_PATTERN = /\(\s*Row\s*(\d+)\s*\)/i;
const STRAW_PATTERN = /Straw\s*['"]([^'"]+)['"]/i;
const WORKSHEET_PATTERN = /(?:in the\s+)?['"]?(Bulk Deposit|Specimen Location|Embryo Straw Details)['"]?\s+worksheet/i;
const MISSING_ROW_PATTERN =
  /^A row in the ['"]?(Bulk Deposit|Specimen Location|Embryo Straw Details)['"]? worksheet\s*\(\s*Row\s*(\d+)\s*\)\s+is missing ['"]([^'"]+)['"]/i;
const REQUIRED_FIELD_PATTERN = /^['"]([^'"]+)['"]\s+is required\b/i;
const CUSTOM_NUMBER_PATTERN = /^['"]([^'"]+)['"]\s+(must be a number|value )\b/i;
const UNRECOGNIZED_VALUE_PATTERN = /^['"]([^'"]+)['"]\s+is not a recognized\b/i;
const COLUMN_WITH_VALUE_PATTERN = /^(.+?)\s+['"]([^'"]+)['"]\s*(.*)$/;

const WORKSHEET_NAMES = {
  bulkDeposit: 'Bulk Deposit',
  specimenLocation: 'Specimen Location',
  embryoStrawDetails: 'Embryo Straw Details',
} as const;

const ERROR_COLUMN_MAPPINGS: { pattern: RegExp; field: string; sheetName?: string }[] = [
  { pattern: /collection site code/i, field: 'Collection Site Code', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /animal code/i, field: 'Animal Code', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /item type/i, field: 'Item Type', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /item unit of measure|unit of measure/i, field: 'Item Unit of Measure', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /lot date/i, field: 'Lot Date', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /lot number/i, field: 'Lot Number', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /total quantity/i, field: 'Total Quantity', sheetName: WORKSHEET_NAMES.bulkDeposit },
  { pattern: /site code/i, field: 'Site Code', sheetName: WORKSHEET_NAMES.specimenLocation },
  { pattern: /tank code/i, field: 'Tank Code', sheetName: WORKSHEET_NAMES.specimenLocation },
  { pattern: /canister code|for canister/i, field: 'Canister Code', sheetName: WORKSHEET_NAMES.specimenLocation },
  { pattern: /straw numbers/i, field: 'Straw Numbers', sheetName: WORKSHEET_NAMES.specimenLocation },
  { pattern: /embryocountperstraw|embryo count per straw/i, field: 'Embryo Count Per Straw', sheetName: WORKSHEET_NAMES.embryoStrawDetails },
  { pattern: /embryo stage/i, field: 'Embryo Stage', sheetName: WORKSHEET_NAMES.embryoStrawDetails },
  { pattern: /embryo grade/i, field: 'Embryo Grade', sheetName: WORKSHEET_NAMES.embryoStrawDetails },
  { pattern: /straw number/i, field: 'Straw Number', sheetName: WORKSHEET_NAMES.embryoStrawDetails },
  { pattern: /\bquantity\b/i, field: 'Quantity' },
  { pattern: /deposit id/i, field: 'Deposit ID' },
];

const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const valuesMatch = (leftValue: string, rightValue: string) => {
  const normalizedLeft = leftValue.trim();
  const normalizedRight = rightValue.trim();
  if (normalizedLeft.toLowerCase() === normalizedRight.toLowerCase()) {
    return true;
  }

  const leftNumber = Number(normalizedLeft);
  const rightNumber = Number(normalizedRight);
  return (
    normalizedLeft !== '' &&
    normalizedRight !== '' &&
    !Number.isNaN(leftNumber) &&
    !Number.isNaN(rightNumber) &&
    leftNumber === rightNumber
  );
};

const removeValidationPrefix = (message: string) => message.replace(PAYLOAD_PREFIX, '').trim();

const isGenericValidationTitle = (value: unknown) =>
  typeof value === 'string' && value.trim().toLowerCase() === GENERIC_VALIDATION_TITLE.toLowerCase();

const getErrorPayload = (error: unknown): ApiErrorPayload | string | undefined => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof responseData === 'string' || (responseData && typeof responseData === 'object')) {
    return responseData as ApiErrorPayload | string;
  }

  if (error && typeof error === 'object' && ('detail' in error || 'title' in error || 'message' in error)) {
    return error as ApiErrorPayload;
  }

  return undefined;
};

const collectErrorMessages = (error: unknown): string[] => {
  const payload = getErrorPayload(error);
  if (!payload) {
    return [];
  }

  if (typeof payload === 'string') {
    return payload.trim() ? [removeValidationPrefix(payload)] : [];
  }

  const messages: string[] = [];
  const addUniqueErrorMessage = (value: unknown) => {
    if (typeof value !== 'string' || !value.trim() || isGenericValidationTitle(value)) {
      return;
    }

    const errorMessage = removeValidationPrefix(value);
    if (errorMessage && !messages.includes(errorMessage)) {
      messages.push(errorMessage);
    }
  };

  addUniqueErrorMessage(payload.detail);
  addUniqueErrorMessage(payload.message);
  addUniqueErrorMessage(payload.title);

  if (payload.errors && typeof payload.errors === 'object') {
    Object.values(payload.errors).forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(addUniqueErrorMessage);
      } else {
        addUniqueErrorMessage(value);
      }
    });
  }

  return messages;
};

export const getBulkDepositValidationMessage = (error: unknown): string => {
  const messages = collectErrorMessages(error);
  return messages[0] ?? '';
};

const getSheetAndColumnFromErrorText = (errorText: string): SheetAndColumn => {
  const worksheetMatch = errorText.match(WORKSHEET_PATTERN);
  const worksheetName = worksheetMatch?.[1];
  const columnMapping = ERROR_COLUMN_MAPPINGS.find(mapping => mapping.pattern.test(errorText));

  return {
    sheetName: worksheetName ?? columnMapping?.sheetName,
    field: columnMapping?.field,
  };
};

const extractFieldFromErrorText = (errorText: string, contextFromMessage: SheetAndColumn): SheetAndColumn => {
  const requiredColumnMatch = errorText.match(REQUIRED_FIELD_PATTERN);
  if (requiredColumnMatch) {
    const columnName = requiredColumnMatch[1].trim();
    const columnFromName = getSheetAndColumnFromErrorText(columnName);
    return {
      field: columnName,
      sheetName: contextFromMessage.sheetName ?? columnFromName.sheetName,
    };
  }

  const customNumberMatch = errorText.match(CUSTOM_NUMBER_PATTERN);
  if (customNumberMatch) {
    return {
      field: customNumberMatch[1].trim(),
      sheetName: contextFromMessage.sheetName ?? WORKSHEET_NAMES.bulkDeposit,
    };
  }

  const unrecognizedValueMatch = errorText.match(UNRECOGNIZED_VALUE_PATTERN);
  if (unrecognizedValueMatch) {
    const columnFromText = getSheetAndColumnFromErrorText(errorText);
    return {
      field: columnFromText.field ?? contextFromMessage.field,
      value: unrecognizedValueMatch[1].trim(),
      sheetName: contextFromMessage.sheetName ?? columnFromText.sheetName,
    };
  }

  const columnWithValueMatch = errorText.match(COLUMN_WITH_VALUE_PATTERN);
  if (columnWithValueMatch) {
    const columnFromText = getSheetAndColumnFromErrorText(`${columnWithValueMatch[1]} ${errorText}`);
    return {
      field: columnFromText.field ?? columnWithValueMatch[1].trim(),
      value: columnWithValueMatch[2].trim(),
      sheetName: contextFromMessage.sheetName ?? columnFromText.sheetName,
    };
  }

  const columnFromText = getSheetAndColumnFromErrorText(errorText);
  return {
    field: columnFromText.field ?? contextFromMessage.field,
    sheetName: contextFromMessage.sheetName ?? columnFromText.sheetName,
  };
};

const parseBulkDepositErrorMessage = (errorMessage: string): BulkDepositValidationError => {
  const missingRowMatch = errorMessage.match(MISSING_ROW_PATTERN);
  if (missingRowMatch) {
    return {
      sheetName: missingRowMatch[1],
      rowNumber: Number(missingRowMatch[2]),
      field: missingRowMatch[3].trim(),
      message: errorMessage,
    };
  }

  const depositId = errorMessage.match(DEPOSIT_ID_PATTERN)?.[1]?.trim();
  const rowNumberMatch = errorMessage.match(ROW_PATTERN);
  const strawNumber = errorMessage.match(STRAW_PATTERN)?.[1]?.trim();
  const sheetAndColumnFromMessage = getSheetAndColumnFromErrorText(errorMessage);
  const detailSeparatorIndex = errorMessage.indexOf(':');
  const errorDetail =
    depositId && detailSeparatorIndex >= 0 && detailSeparatorIndex > errorMessage.indexOf(depositId)
      ? errorMessage.slice(detailSeparatorIndex + 1).trim()
      : errorMessage.replace(DEPOSIT_ID_PATTERN, '').replace(ROW_PATTERN, '').trim();

  const errorDetails = extractFieldFromErrorText(errorDetail, sheetAndColumnFromMessage);

  return {
    depositId,
    strawNumber,
    rowNumber: rowNumberMatch ? Number(rowNumberMatch[1]) : undefined,
    field: errorDetails.field ?? (depositId ? 'Deposit ID' : undefined),
    value: errorDetails.value,
    sheetName: errorDetails.sheetName ?? sheetAndColumnFromMessage.sheetName,
    message: errorMessage,
  };
};

const splitBulkDepositErrorMessages = (message: string): string[] => {
  const errorMessage = removeValidationPrefix(message);
  if (!errorMessage) {
    return [];
  }

  const errorMessages = errorMessage.split(
    /(?=Deposit\s*ID\s*['"][^'"]+['"](?:\s*\(|\s+Straw\s*['"]|\s*:|\s+in the )|A row in the )/i,
  );
  return errorMessages.map(segment => segment.replace(/^[;:\s]+|[;\s]+$/g, '').trim()).filter(Boolean);
};

export const parseBulkDepositValidationErrors = (error: unknown): BulkDepositValidationError[] => {
  const validationErrors: BulkDepositValidationError[] = [];
  const uniqueErrorKeys = new Set<string>();

  collectErrorMessages(error).forEach(message => {
    const errorMessages = splitBulkDepositErrorMessages(message);
    const parsedErrors = (errorMessages.length ? errorMessages : [message]).map(parseBulkDepositErrorMessage);

    parsedErrors.forEach(validationError => {
      const errorKey = [
        validationError.depositId ?? '',
        validationError.field ?? '',
        validationError.value ?? '',
        validationError.rowNumber ?? '',
        validationError.sheetName ?? '',
        validationError.message,
      ].join('|');

      if (uniqueErrorKeys.has(errorKey)) {
        return;
      }

      uniqueErrorKeys.add(errorKey);
      validationErrors.push(validationError);
    });
  });

  return validationErrors;
};

const findColumnIndexByHeader = (headers: string[], columnName?: string, sheetName?: string) => {
  if (!columnName) {
    return -1;
  }

  const normalizedColumnName = normalizeHeader(columnName);
  const normalizedHeaderAliases = new Set([normalizedColumnName]);

  if (normalizedColumnName === 'quantity' || normalizedColumnName === 'totalquantity') {
    if (sheetName && normalizeHeader(sheetName) === normalizeHeader(WORKSHEET_NAMES.bulkDeposit)) {
      normalizedHeaderAliases.add('totalquantity');
    } else if (sheetName && normalizeHeader(sheetName) === normalizeHeader(WORKSHEET_NAMES.specimenLocation)) {
      normalizedHeaderAliases.add('quantity');
    } else {
      normalizedHeaderAliases.add('quantity');
      normalizedHeaderAliases.add('totalquantity');
    }
  }

  if (normalizedColumnName === 'embryocountperstraw') {
    normalizedHeaderAliases.add('embryocountperstraw');
  }

  const exactColumnIndex = headers.findIndex(header => normalizedHeaderAliases.has(normalizeHeader(header)));
  if (exactColumnIndex >= 0) {
    return exactColumnIndex;
  }

  const headerAliases = Array.from(normalizedHeaderAliases);
  return headers.findIndex(header => {
    const normalizedHeader = normalizeHeader(header);
    return headerAliases.some(alias => normalizedHeader.includes(alias) || alias.includes(normalizedHeader));
  });
};

const getSheetsMatchingError = (workbook: ParsedExcelWorkbook, error: BulkDepositValidationError): ParsedExcelSheet[] => {
  if (!error.sheetName) {
    return workbook.sheets;
  }

  const normalizedErrorSheetName = normalizeHeader(error.sheetName);
  const matchingSheets = workbook.sheets.filter(sheet => {
    const normalizedSheetName = normalizeHeader(sheet.name);
    return normalizedSheetName === normalizedErrorSheetName || normalizedSheetName.includes(normalizedErrorSheetName);
  });

  return matchingSheets.length ? matchingSheets : workbook.sheets;
};

export const mapValidationErrorsToCells = (
  workbook: ParsedExcelWorkbook | null,
  errors: BulkDepositValidationError[],
): BulkDepositHighlightedCell[] => {
  if (!workbook || !errors.length) {
    return [];
  }

  const highlightedCells: BulkDepositHighlightedCell[] = [];

  errors.forEach((error, errorIndex) => {
    getSheetsMatchingError(workbook, error).forEach(sheet => {
      const depositIdColumnIndex = findColumnIndexByHeader(sheet.headers, 'Deposit ID', sheet.name);
      const strawNumberColumnIndex = findColumnIndexByHeader(sheet.headers, 'Straw Number', sheet.name);
      const errorColumnIndex = findColumnIndexByHeader(sheet.headers, error.field, error.sheetName ?? sheet.name);
      const highlightedColumnIndex = errorColumnIndex >= 0 ? errorColumnIndex : depositIdColumnIndex;
      if (highlightedColumnIndex < 0) {
        return;
      }

      sheet.rows.forEach((row, rowIndex) => {
        const excelRowNumber = sheet.excelRowNumbers?.[rowIndex];
        const isErrorRow = error.rowNumber != null && excelRowNumber === error.rowNumber;
        const sheetContainsErrorRow = error.rowNumber != null && sheet.excelRowNumbers?.includes(error.rowNumber);
        const rowDepositId = depositIdColumnIndex >= 0 ? row[depositIdColumnIndex] ?? '' : '';
        const matchesDepositId =
          Boolean(error.depositId) && depositIdColumnIndex >= 0 && valuesMatch(rowDepositId, error.depositId!);
        const matchesStrawNumber =
          Boolean(error.strawNumber) &&
          strawNumberColumnIndex >= 0 &&
          valuesMatch(row[strawNumberColumnIndex] ?? '', error.strawNumber!);
        const matchesErrorValue =
          error.value !== undefined && errorColumnIndex >= 0 && valuesMatch(row[errorColumnIndex] ?? '', error.value);

        if (sheetContainsErrorRow) {
          if (!isErrorRow) {
            return;
          }
        } else if (error.depositId || error.strawNumber || error.value) {
          if (!matchesDepositId && !matchesStrawNumber && !matchesErrorValue) {
            return;
          }
        } else {
          return;
        }

        highlightedCells.push({
          sheetName: sheet.name,
          rowIndex,
          columnIndex: highlightedColumnIndex,
          message: error.message,
          errorIndex,
        });
      });
    });
  });

  return highlightedCells;
};

export const getHighlightedCellKey = (sheetName: string, rowIndex: number, columnIndex: number) =>
  `${sheetName}-${rowIndex}-${columnIndex}`;
